import { networkAtom } from "@/atoms/settings"
import { ButtonConnect } from "@/components/buttonConnect"
import { Box, Divider, Link, Stack } from "@mui/material"
import { BlockchainNetwork } from "@renec-foundation/relend-adapter"
import { useAtomValue } from "jotai"
import { usePathname, useSearchParams } from "next/navigation"
import { memo, useMemo } from "react"

import LogoIcon from "@/assets/icons/lending-logo.svg"
import Button from "@/components/base/Button"
import navbarLinks from "@/constants/navbarLinks"
import useTrans from "@/hooks/useTrans"
import Image from "next/image"
import NextLink from "next/link"
import LanguageMenu from "../language"
import LendingMenuItem from "./MenuItem"
import * as styles from "./styles"

interface MenuItem {
  href: string
  isActive: boolean
  label: string
  dataTestId: string
  target?: React.HTMLAttributeAnchorTarget
}

const View = () => {
  const trans = useTrans()
  const routePathname = usePathname()
  const searchParams = useSearchParams()
  const network = useAtomValue(networkAtom)

  const isRenecNetwork = network === BlockchainNetwork.RENEC

  const isHome = routePathname === "/home"

  const currentSearchParams = new URLSearchParams(Array.from(searchParams.entries()))
  const currentSearchParamsString = currentSearchParams.toString()
  const query = currentSearchParamsString ? `?${currentSearchParamsString}` : ""

  const renecMenus: MenuItem[] = useMemo(
    () => [
      {
        href: `${navbarLinks.dashboard}${query}`,
        isActive: routePathname === navbarLinks.dashboard,
        label: trans.header.dashboard,
        dataTestId: "menu-dashboard",
      },
      {
        href: `${navbarLinks.referral}${query}`,
        isActive: routePathname === navbarLinks.referral,
        label: trans.header.referral,
        dataTestId: "menu-referral",
      },
      {
        href: `${navbarLinks.liquidations}`,
        isActive: routePathname === navbarLinks.liquidations,
        label: trans.header.liquidations,
        dataTestId: "menu-liquidations",
      },
      {
        href: `${navbarLinks.staking}`,
        isActive: routePathname === navbarLinks.staking,
        label: trans.header.staking,
        dataTestId: "menu-staking",
      },
      {
        href: `${navbarLinks.docs}`,
        isActive: false,
        label: trans.header.docs,
        dataTestId: "menu-docs",
        target: "_blank",
      },
    ],
    [query, routePathname, trans],
  )

  const solanaMenus: MenuItem[] = useMemo(
    () => [
      {
        href: `${navbarLinks.dashboard}${query}`,
        isActive: routePathname === navbarLinks.dashboard,
        label: trans.header.dashboard,
        dataTestId: "menu-dashboard",
      },
      {
        href: `${navbarLinks.referral}${query}`,
        isActive: routePathname === navbarLinks.referral,
        label: trans.header.referral,
        dataTestId: "menu-referral",
      },
      {
        href: `${navbarLinks.docs}`,
        isActive: false,
        label: trans.header.docs,
        dataTestId: "menu-docs",
        target: "_blank",
      },
    ],
    [query, routePathname, trans],
  )

  const menus = useMemo(() => (isRenecNetwork ? renecMenus : solanaMenus), [isRenecNetwork, renecMenus, solanaMenus])

  return (
    <>
      <Stack direction='row' spacing={1}>
        <Link underline='none' component={NextLink} href={navbarLinks.home} data-testid='menu-logo'>
          <Box sx={styles.logoWrapperStyle}>
            <Image src={LogoIcon} alt='relend-icon' width={48} height={48} />
          </Box>
        </Link>
      </Stack>
      <Box className='flex items-center gap-6 flex-grow ml-14'>
        {menus.map((menu) => (
          <LendingMenuItem
            key={menu.dataTestId}
            href={menu.href}
            label={menu.label}
            dataTestId={menu.dataTestId}
            isActive={menu.isActive}
            target={menu?.target}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", flexGrow: 0 }}>
        <Stack direction='row' spacing={1} alignItems='center'>
          {isHome ? (
            <Button variant='primary' size='md' href={navbarLinks.dashboard}>
              {trans.home.launch_app}
            </Button>
          ) : (
            <ButtonConnect />
          )}
          <Divider
            orientation='vertical'
            flexItem
            style={{
              height: "100%",
              width: "1px",
            }}
          />
          <LanguageMenu />
        </Stack>
      </Box>
    </>
  )
}

View.displayName = "MenuDesktopView"
export default memo(View)
